import { Box, Typography } from "@mui/material";
import { destinations } from "../../../../data/data";
import { useConfigurations } from "../../../../../../context/ConfigurationsContext/ConfigurationsContext";
import { usePrincipalPage } from "../../../../../../context/PrincipalPageContext/PrincipalPageContext";
import location from "../../../../../../assets/images/HomePage/Icons/pin-alt.svg";

type DestinationItem = {
  name: string;
  image: string;
  description: string;
};

type MosaicSlot = {
  key: string;
  left: number;
  top: number;
  width: number;
  height: number;
  place: DestinationItem;
};

type ResponsiveSlot = {
  key: string;
  height: number;
  place: DestinationItem;
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const PrincipalPageDestinations = () => {
  const principalpageconfigs = usePrincipalPage();
  const configurations = useConfigurations();
  const txt = configurations.txt[configurations.currentlenguage];
  const places = destinations(txt);
  const byName = new Map(places.map((p) => [normalize(p.name), p]));
  const getPlace = (fallbackIndex: number, ...aliases: string[]) =>
    aliases.map((alias) => byName.get(normalize(alias))).find(Boolean) || places[fallbackIndex];

  const mosaicSlots: MosaicSlot[] = [
    { key: "cancun", left: 0, top: 0, width: 210, height: 186.5, place: getPlace(0, "Cancún", "Cancun") },
    {
      key: "playa",
      left: 226,
      top: 0,
      width: 437,
      height: 187,
      place: getPlace(1, "Playa del Carmen"),
    },
    { key: "tulum", left: 678, top: 0, width: 324, height: 391, place: getPlace(2, "Tulum") },
    { key: "chetumal", left: 1017, top: 0, width: 324, height: 149, place: getPlace(3, "Chetumal") },
    { key: "bacalar", left: 0, top: 204, width: 324, height: 391, place: getPlace(4, "Bacalar") },
    { key: "cozumel", left: 339, top: 204, width: 324, height: 187, place: getPlace(5, "Cozumel") },
    { key: "isla", left: 1017, top: 169, width: 324, height: 257, place: getPlace(6, "Isla Mujeres") },
    { key: "holbox", left: 340, top: 408, width: 210, height: 187, place: getPlace(7, "Holbox") },
    { key: "pmorelos", left: 565, top: 408, width: 210, height: 187, place: getPlace(10, "Puerto Morelos") },
    { key: "mayakaan", left: 790, top: 408, width: 210, height: 187, place: getPlace(9, "Maya Ka'an", "Maya Kaan") },
    { key: "mahahual", left: 1017, top: 446, width: 324, height: 149, place: getPlace(8, "Mahahual") },
  ];

  const mobileLeftSlots: ResponsiveSlot[] = [
    { key: "cancun-m", height: 151, place: getPlace(0, "CancÃºn", "Cancun") },
    { key: "bacalar-m", height: 207, place: getPlace(4, "Bacalar") },
    { key: "tulum-m", height: 311, place: getPlace(2, "Tulum") },
    { key: "cozumel-m", height: 151, place: getPlace(5, "Cozumel") },
    { key: "pmorelos-m", height: 207, place: getPlace(10, "Puerto Morelos") },
    { key: "holbox-m", height: 121, place: getPlace(7, "Holbox") },
  ];

  const mobileRightSlots: ResponsiveSlot[] = [
    { key: "playa-m", height: 311, place: getPlace(1, "Playa del Carmen") },
    { key: "chetumal-m", height: 151, place: getPlace(3, "Chetumal") },
    { key: "isla-m", height: 207, place: getPlace(6, "Isla Mujeres") },
    { key: "mahahual-m", height: 311, place: getPlace(8, "Mahahual") },
    { key: "mayakaan-m", height: 151, place: getPlace(9, "Maya Ka'an", "Maya Kaan") },
  ];

  const renderCard = (slot: ResponsiveSlot, compactLabel = false) => (
    <Box
      key={slot.key}
      aria-label={`${slot.place.name}: ${slot.place.description}`}
      tabIndex={0}
      sx={{
        borderRadius: "8px",
        boxShadow: "0 4px 6px -4px rgba(19,25,39,0.12), 0 8px 8px -4px rgba(19,25,39,0.08)",
        overflow: "hidden",
        position: "relative",
        "&:focus-within .destinationInfo, &:hover .destinationInfo": {
          opacity: 1,
          transform: "translateY(0)",
        },
        "&:focus-within .destinationOverlay, &:hover .destinationOverlay": {
          opacity: 1,
        },
      }}
    >
      <Box
        component="img"
        src={slot.place.image}
        alt={slot.place.name}
        loading="lazy"
        decoding="async"
        sx={{
          display: "block",
          height: `${slot.height}px`,
          objectFit: "cover",
          width: "100%",
        }}
      />
      <Box
        className="destinationOverlay"
        sx={{
          background: "linear-gradient(180deg, rgba(0,89,106,0.08) 0%, rgba(0,89,106,0.88) 72%)",
          inset: 0,
          opacity: 0,
          position: "absolute",
          transition: "opacity 180ms ease",
        }}
      />

      <Box
        className="destinationInfo"
        sx={{
          bottom: compactLabel ? "12px" : "14px",
          left: compactLabel ? "8px" : "12px",
          opacity: 0,
          position: "absolute",
          right: "8px",
          transform: "translateY(8px)",
          transition: "opacity 180ms ease, transform 180ms ease",
        }}
      >
        <Box sx={{ alignItems: "center", color: "#fff", display: "flex", gap: "6px" }}>
          <Box
            component="img"
            src={location}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ height: compactLabel ? "20px" : "22px", width: compactLabel ? "20px" : "22px" }}
          />
          <Typography
            sx={{
              color: "#FFFFFF",
              fontFamily: "Inter",
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "20px",
              textTransform: "uppercase",
            }}
          >
            {slot.place.name}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: "#FFFFFF",
            fontFamily: "Inter",
            fontSize: compactLabel ? "14px" : "13px",
            fontWeight: 400,
            lineHeight: "20px",
            mt: 0.5,
          }}
        >
          {slot.place.description}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      ref={principalpageconfigs.destinosRef}
      sx={{
        backgroundColor: "#F6F6F6",
        px: { xs: 2, md: "49px" },
        py: { xs: 5, md: "50px" },
      }}
    >
      <Typography
        sx={{
          color: "#4197A8",
          fontFamily: "Inter",
          fontSize: { xs: "30px", md: "32px" },
          fontWeight: 600,
          lineHeight: { xs: "34px", md: "30px" },
          mb: { xs: 4, md: "30px" },
          textAlign: "center",
          textTransform: "uppercase",
        }}
      >
        {txt[122]}
      </Typography>

      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          margin: "0 auto",
          maxWidth: "1341px",
          minHeight: "595px",
          position: "relative",
          width: "100%",
        }}
      >
        {mosaicSlots.map((slot) => (
          <Box
            key={slot.key}
            aria-label={`${slot.place.name}: ${slot.place.description}`}
            tabIndex={0}
            sx={{
              borderRadius: "8px",
              boxShadow: "0 4px 6px -4px rgba(19,25,39,0.12), 0 8px 8px -4px rgba(19,25,39,0.08)",
              height: `${slot.height}px`,
              left: `${slot.left}px`,
              overflow: "hidden",
              position: "absolute",
              top: `${slot.top}px`,
              width: `${slot.width}px`,
              "&:focus-within .destinationInfo, &:hover .destinationInfo": {
                opacity: 1,
                transform: "translateY(0)",
              },
              "&:focus-within .destinationOverlay, &:hover .destinationOverlay": {
                opacity: 1,
              },
            }}
          >
            <Box
              component="img"
              src={slot.place.image}
              alt={slot.place.name}
              loading="lazy"
              decoding="async"
              sx={{
                height: "100%",
                objectFit: "cover",
                width: "100%",
              }}
            />
            <Box
              className="destinationOverlay"
              sx={{
                background: "linear-gradient(180deg, rgba(0,89,106,0.08) 0%, rgba(0,89,106,0.88) 72%)",
                inset: 0,
                opacity: 0,
                position: "absolute",
                transition: "opacity 180ms ease",
              }}
            />

            <Box
              className="destinationInfo"
              sx={{
                bottom: "14px",
                left: "20px",
                opacity: 0,
                position: "absolute",
                right: "16px",
                transform: "translateY(8px)",
                transition: "opacity 180ms ease, transform 180ms ease",
              }}
            >
              <Typography
                sx={{
                  alignItems: "center",
                  color: "#FFFFFF",
                  display: "flex",
                  fontFamily: "Candara",
                  fontSize: slot.width < 260 ? "20px" : "24px",
                  fontWeight: 600,
                  lineHeight: "30px",
                  textTransform: "uppercase",
                }}
              >
                <Box
                  component="img"
                  src={location}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  sx={{ height: "24px", mr: "10px", width: "24px" }}
                />
                {slot.place.name}
              </Typography>
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontFamily: "Candara",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                {slot.place.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: { xs: "grid", lg: "none" },
          gap: "10px",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          margin: "0 auto",
          maxWidth: { xs: "390px", sm: "760px", md: "900px" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {mobileLeftSlots.map((slot) => renderCard(slot))}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {mobileRightSlots.map((slot) => renderCard(slot, true))}
        </Box>
      </Box>
    </Box>
  );
};

export default PrincipalPageDestinations;
